import RestfulUtil from "@/core/restful/RestfulUtil"
import { isDiscoDomain } from "@/core/route/util/routeUtils"
import { stopImpersonatingTestUser } from "@/product/util/hook/useInitImpersonateTestUser"
import { INVALID_USER_LINK_KEY_ERROR } from "@/relay/RelayEnvironment"
import { GlobalID } from "@/relay/RelayTypes"

export type AiApiGenerateTextArgs = {
  organizationId: GlobalID
  productId?: GlobalID
  responseType: AiApiGenerateTextResponseType
  prompt: string
  referenceUrl?: string
  referenceEmbeddingSourceIds?: GlobalID[]
  referenceModuleContentUsageId?: GlobalID
}
export type AiApiGenerateTextResponseType = "title" | "description" | "email"
export type AiApiGeneratePostArgs = {
  feedId: GlobalID
  template?: AiApiGeneratePostTemplate | null
  responseType: AiApiGeneratePostResponseType
  postTitle?: string | null // when generating post body, optionally pass an existing title
  prompt?: string | null // user prompt to guide the post generation
  referenceUrls?: string[] | null // optional reference URLs for additional context
  referenceEmbeddingSourceIds?: GlobalID[] | null
  searchWeb?: boolean | null // whether to use web search capabilities
  previousResearchOutput?: string // research context from a previous search
}
export type AiApiGeneratePostTemplate = "inactive" | "default"
export type AiApiGeneratePostResponseType = "title" | "body"
export type AiApiGenerateMessageArgs = {
  channelId: string
  template: AiApiGenerateMessageTemplate
}
export type AiApiGenerateMessageTemplate = "inactive" | "default"

namespace AiApi {
  export async function request(
    path: string,
    body: Record<string, any>,
    ac?: AbortController
  ) {
    const baseUrl = isDiscoDomain(window.location.host) ? GRAPHQL_URL : "/"
    const abortController = ac || new AbortController()

    const headers: Record<string, string> = {
      Accept: "application/json, text/plain, */*",
      "Content-Type": "application/json",
    }

    if (window.discoUlKey) {
      headers["X-UL-Key"] = window.discoUlKey
    }

    const response = await fetch(`${baseUrl}${path}`, {
      method: "post",
      credentials: "include",
      headers,
      body: JSON.stringify(body),
      signal: abortController.signal,
    })

    const clonedResponse = response.clone() // Clone the response so we can read the body twice
    if (RestfulUtil.hasError(clonedResponse)) {
      const readableResponse = await clonedResponse.json()
      const errorMessage = readableResponse.error

      if (errorMessage === INVALID_USER_LINK_KEY_ERROR) {
        stopImpersonatingTestUser()
      }
    }

    return { response, abortController }
  }

  export function generateBrainSearchResponse(
    {
      brainSearchId,
      messageText,
      assistantMessageId,
    }: {
      brainSearchId: GlobalID
      messageText: string
      assistantMessageId: GlobalID
    },
    abortController?: AbortController
  ) {
    return request(
      "ai/generate/brain-search",
      {
        brainSearchId,
        messageText,
        assistantMessageId,
      },
      abortController
    )
  }

  export function generateText(input: AiApiGenerateTextArgs) {
    return request("ai/generate/text", input)
  }

  export function generatePost(input: AiApiGeneratePostArgs) {
    return request("ai/generate/post", input)
  }

  export function generateMessage(input: AiApiGenerateMessageArgs) {
    return request("ai/generate/message", input)
  }

  export function generateSuggestedPrompts(input?: { existingPrompts?: string[] }) {
    return request("ai/generate/suggested-prompts", input || {})
  }
}

export default AiApi
